import {
  COLORS,
  OVERLAYS,
  SCENE_H,
  SCENE_W,
  composeCharacter,
  composePlace,
  composePlaceFront,
  composeRoom,
  flyPositions,
  friendLook,
  frontItems,
  type CharacterLook,
  type Pose,
  type RoomView,
} from "@squire/sprites";
import type { DirectorFrame } from "../avatar/director.js";
import { SurfaceCache, bitmapToSurface, type Surface } from "./bitmapCanvas.js";

type Ctx2D = CanvasRenderingContext2D;

export interface SceneInput {
  room: RoomView;
  look: CharacterLook;
  avatar: DirectorFrame;
  now: number;
  /** 0..1 darkness for night dimming (wall profile). */
  dim: number;
}

/**
 * Draws the 192×120 scene. The room is composed only when something visible changes;
 * avatar frames are cached in a bounded LRU.
 */
export class SceneRenderer {
  private roomKey = "";
  private roomSurface: Surface | null = null;
  private readonly frames = new SurfaceCache(160);
  private readonly overlays = new SurfaceCache(16);
  private front: Surface[] = [];
  private frontPos: [number, number][] = [];
  /** Away places: background and front layer, one cached per place and sky bucket. */
  private placeKey = "";
  private placeSurface: Surface | null = null;
  private placeFront: Surface | null = null;

  constructor(private readonly ctx: Ctx2D) {
    ctx.imageSmoothingEnabled = false;
  }

  draw(input: SceneInput): void {
    if (input.avatar.place !== "home") this.drawPlace(input);
    else this.drawHome(input);
    this.drawEffects(input);
  }

  private drawPlace(input: SceneInput): void {
    const { ctx } = this;
    const a = input.avatar;
    const place = a.place as Exclude<typeof a.place, "home">;
    const key = `${place}|${input.room.minutes}|${input.room.seed}`;
    if (key !== this.placeKey || !this.placeSurface) {
      this.placeKey = key;
      this.placeSurface = bitmapToSurface(
        composePlace(place, { minutes: input.room.minutes, seed: input.room.seed }),
      );
      const front = composePlaceFront(place);
      this.placeFront = front ? bitmapToSurface(front) : null;
    }
    ctx.drawImage(this.placeSurface as CanvasImageSource, 0, 0);
    if (a.friend) {
      const f = a.friend;
      const fl = friendLook(f.seed);
      // Never twins: a different shirt than the companion.
      const look = fl.shirt === input.look.shirt ? { ...fl, shirt: (fl.shirt + 3) % 8 } : fl;
      this.drawCharacter(look, `fr${f.seed}`, 1, f.anim, f.frameIndex, f.pose, f.x, f.y, f.flip, false);
    }
    if (!a.hidden)
      this.drawCharacter(
        input.look,
        "me",
        a.stage,
        a.anim,
        a.frameIndex,
        a.pose,
        a.x,
        a.y,
        a.flip,
        a.readingGlasses,
      );
    if (this.placeFront) ctx.drawImage(this.placeFront as CanvasImageSource, 0, 0);
  }

  private drawHome(input: SceneInput): void {
    const { ctx } = this;
    const key = JSON.stringify(input.room);
    if (key !== this.roomKey || !this.roomSurface) {
      this.roomKey = key;
      this.roomSurface = bitmapToSurface(composeRoom(input.room));
      const fronts = frontItems(input.room.items);
      this.front = fronts.map((f) => bitmapToSurface(f.sprite));
      this.frontPos = fronts.map((f) => [f.x, f.y]);
    }
    ctx.drawImage(this.roomSurface as CanvasImageSource, 0, 0);

    ctx.fillStyle = COLORS.ink;
    for (const [fx, fy] of flyPositions(input.room.mess, input.now)) ctx.fillRect(fx, fy, 1, 1);

    const a = input.avatar;
    if (!a.hidden)
      this.drawCharacter(
        input.look,
        "me",
        a.stage,
        a.anim,
        a.frameIndex,
        a.pose,
        a.x,
        a.y,
        a.flip,
        a.readingGlasses,
      );

    this.front.forEach((s, i) =>
      ctx.drawImage(s as CanvasImageSource, this.frontPos[i]![0], this.frontPos[i]![1]),
    );
  }

  /** One character frame (cached), mirrored when flipped, with its overlay bubble. */
  private drawCharacter(
    look: CharacterLook,
    who: string,
    stage: number,
    anim: string,
    frameIndex: number,
    pose: Pose,
    x: number,
    y: number,
    flip: boolean,
    readingGlasses: boolean,
  ): void {
    const { ctx } = this;
    const lookKey = `${look.skin}${look.hair}${look.hairColor}${look.beard}${look.glasses ? 1 : 0}${look.shirt}`;
    const fkey = `${who}|${lookKey}|${stage}|${anim}|${frameIndex}|${readingGlasses ? 1 : 0}`;
    const surface = this.frames.get(fkey, () =>
      bitmapToSurface(composeCharacter(look, stage, pose, { readingGlasses })),
    );
    const s = surface as CanvasImageSource & { width: number; height: number };
    const ox = 24;
    const oy = 46;
    if (flip) {
      ctx.save();
      ctx.translate(x + ox + 1, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(s, 0, y - oy);
      ctx.restore();
    } else {
      ctx.drawImage(s, x - ox, y - oy);
    }
    const ov = pose.overlay;
    if (ov) {
      const bm = OVERLAYS[ov];
      const os = this.overlays.get(ov, () => bitmapToSurface(bm)) as CanvasImageSource;
      const above = pose.orient === "supine" ? 12 : pose.orient === "prone" ? 16 : 38;
      ctx.drawImage(os, x + 5, y - above - bm.h);
    }
  }

  private drawEffects(input: SceneInput): void {
    const { ctx } = this;
    const a = input.avatar;
    if (a.flash) {
      ctx.globalAlpha = 0.35 + 0.25 * Math.sin(input.now / 60);
      ctx.fillStyle = COLORS.paper;
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      ctx.globalAlpha = 1;
    }
    if (input.dim > 0) {
      ctx.globalAlpha = Math.min(0.85, input.dim);
      ctx.fillStyle = COLORS.night;
      ctx.fillRect(0, 0, SCENE_W, SCENE_H);
      ctx.globalAlpha = 1;
    }
  }

  stats(): { frames: number } {
    return { frames: this.frames.size };
  }
}
